import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["noteContent", "noteTitle", "saveIndicator", "noteCard"]
  static values = {
    noteId: String,
    clientId: String,
    autosaveDelay: { type: Number, default: 2000 }
  }

  connect() {
    this.unsavedChanges = false
    this.autosaveTimer = null
  }

  disconnect() {
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer)
    }
    // Save any unsaved changes before disconnecting
    if (this.unsavedChanges) {
      this.saveNote()
    }
  }

  contentChanged() {
    this.unsavedChanges = true
    this.showSavingIndicator("Unsaved changes...")

    // Clear existing timer
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer)
    }

    // Set new timer for autosave
    this.autosaveTimer = setTimeout(() => {
      this.saveNote()
    }, this.autosaveDelayValue)
  }

  async saveNote() {
    if (!this.hasNoteContentTarget) return

    const content = this.noteContentTarget.value
    const title = this.hasNoteTitleTarget ? this.noteTitleTarget.value : null

    this.showSavingIndicator("Saving...")

    try {
      if (this.noteIdValue) {
        // Update existing note
        await this.updateNote(content, title)
      } else {
        // Create new note
        await this.createNote(content, title)
      }

      this.unsavedChanges = false
      this.showSavingIndicator("Saved")
      setTimeout(() => this.hideSavingIndicator(), 2000)
    } catch (error) {
      console.error("Failed to save note:", error)
      this.showSavingIndicator("Failed to save")
    }
  }

  async createNote(content, title) {
    const response = await fetch(`/sm/clients/${this.clientIdValue}/notes`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        note: { content, title }
      })
    })

    if (response.ok) {
      const data = await response.json()
      this.noteIdValue = data.id
    } else {
      throw new Error("Failed to create note")
    }
  }

  async updateNote(content, title) {
    const response = await fetch(`/sm/clients/${this.clientIdValue}/notes/${this.noteIdValue}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        note: { content, title }
      })
    })

    if (!response.ok) {
      throw new Error("Failed to update note")
    }
  }

  showSavingIndicator(message) {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.textContent = message
      this.saveIndicatorTarget.classList.remove("hidden")
    }
  }

  hideSavingIndicator() {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.classList.add("hidden")
    }
  }

  expandNote(event) {
    const card = event.currentTarget.closest("[data-notes-target='noteCard']")
    if (card) {
      card.classList.toggle("expanded")
      const content = card.querySelector("[data-note-content]")
      if (content) {
        content.classList.toggle("line-clamp-3")
      }
    }
  }

  collapseNote(event) {
    const card = event.currentTarget.closest("[data-notes-target='noteCard']")
    if (card) {
      card.classList.remove("expanded")
      const content = card.querySelector("[data-note-content]")
      if (content) {
        content.classList.add("line-clamp-3")
      }
    }
  }

  async deleteNote(event) {
    const noteId = event.currentTarget.dataset.noteId

    if (!confirm("Are you sure you want to delete this note?")) {
      return
    }

    try {
      const response = await fetch(`/sm/clients/${this.clientIdValue}/notes/${noteId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const card = event.currentTarget.closest("[data-notes-target='noteCard']")
        if (card) {
          card.remove()
        }
      }
    } catch (error) {
      console.error("Failed to delete note:", error)
    }
  }

  newNote() {
    // Show the new note form or expand an inline editor
    const form = this.element.querySelector("[data-new-note-form]")
    if (form) {
      form.classList.remove("hidden")
      const input = form.querySelector("textarea")
      if (input) input.focus()
    }
  }

  cancelNewNote() {
    const form = this.element.querySelector("[data-new-note-form]")
    if (form) {
      form.classList.add("hidden")
      const inputs = form.querySelectorAll("input, textarea")
      inputs.forEach(input => input.value = "")
    }
  }

  searchNotes(event) {
    const query = event.target.value.toLowerCase()
    const notes = this.element.querySelectorAll("[data-notes-target='noteCard']")

    notes.forEach(note => {
      const content = note.textContent.toLowerCase()
      if (content.includes(query)) {
        note.classList.remove("hidden")
      } else {
        note.classList.add("hidden")
      }
    })
  }
}
